<template>
  <div class="print-page-wrap">

    <!-- Screen toolbar (hidden when printing) -->
    <div class="print-toolbar no-print">
      <button class="toolbar-btn" @click="$router.go(-1)">
        <font-awesome-icon icon="arrow-left" class="me-1" /> Back
      </button>
      <div class="toolbar-right">
        <button class="toolbar-btn toolbar-btn--primary" @click="printDoc">
          <font-awesome-icon icon="print" class="me-1" /> Print
        </button>
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="print-loading no-print">
      <div class="spinner"></div>
      <p>Loading purchase order…</p>
    </div>

    <!-- Document -->
    <div v-else-if="po" class="document" id="printMe">

      <!-- Document header -->
      <div class="doc-header">
        <div class="doc-brand">
          <img v-if="business.logo" :src="hostName + business.logo" class="doc-logo" alt="Logo" />
          <div v-else class="doc-logo-placeholder">{{ (business.name || '?').charAt(0) }}</div>
          <div class="doc-brand-text">
            <div class="doc-company-name">{{ business.name || '—' }}</div>
            <div class="doc-company-sub" v-if="business.address">{{ business.address }}</div>
            <div class="doc-company-sub" v-if="business.email">{{ business.email }}</div>
          </div>
        </div>
        <div class="doc-title-block">
          <div class="doc-title">PURCHASE ORDER</div>
          <table class="doc-meta-table">
            <tr>
              <td class="dmt-label">PO Number</td>
              <td class="dmt-value mono">{{ po.po_number }}</td>
            </tr>
            <tr>
              <td class="dmt-label">Date Issued</td>
              <td class="dmt-value">{{ po.issued_at ? formatDate(po.issued_at) : formatDate(po.created_at) }}</td>
            </tr>
            <tr v-if="po.expected_delivery_date">
              <td class="dmt-label">Expected Delivery</td>
              <td class="dmt-value">{{ formatDate(po.expected_delivery_date) }}</td>
            </tr>
            <tr>
              <td class="dmt-label">Status</td>
              <td class="dmt-value">
                <span :class="['status-pill', `status-pill--${po.status}`]">{{ formatStatus(po.status) }}</span>
              </td>
            </tr>
            <tr v-if="po.pr">
              <td class="dmt-label">From PR</td>
              <td class="dmt-value mono-sm">{{ po.pr.pr_number }}</td>
            </tr>
          </table>
        </div>
      </div>

      <div class="doc-divider"></div>

      <!-- Vendor + Delivery two-column -->
      <div class="doc-two-col">
        <div class="doc-address-block">
          <div class="doc-address-head">VENDOR</div>
          <div class="doc-address-name">{{ po.vendor?.name || '—' }}</div>
          <div class="doc-address-line" v-if="po.vendor?.primaryContact?.email">
            {{ po.vendor.primaryContact.email }}
          </div>
          <div class="doc-address-line" v-if="po.vendor?.primaryContact?.phone">
            {{ po.vendor.primaryContact.phone }}
          </div>
        </div>
        <div class="doc-address-block" v-if="po.delivery_address">
          <div class="doc-address-head">DELIVER TO</div>
          <div class="doc-address-line" style="white-space:pre-line">{{ po.delivery_address }}</div>
        </div>
        <div class="doc-terms-block">
          <div class="doc-address-head">ORDER INFO</div>
          <div class="doc-terms-row" v-if="po.payment_terms">
            <span class="doc-terms-label">Payment Terms</span>
            <span class="doc-terms-val">{{ po.payment_terms }}</span>
          </div>
          <div class="doc-terms-row">
            <span class="doc-terms-label">Currency</span>
            <span class="doc-terms-val">{{ po.currency }}</span>
          </div>
        </div>
      </div>

      <div class="doc-divider doc-divider--dashed"></div>

      <!-- Title -->
      <div class="doc-section-label">{{ po.title }}</div>

      <!-- Line items -->
      <div class="table-section-head">
        <span class="table-section-title">Line Items</span>
        <span class="table-count-badge">{{ po.lines?.length || 0 }} item{{ (po.lines?.length || 0) !== 1 ? 's' : '' }}</span>
      </div>
      <div class="doc-table-wrap">
        <table class="doc-table">
          <colgroup>
            <col class="col-no" />
            <col class="col-desc" />
            <col class="col-spec" />
            <col class="col-qty" />
            <col class="col-uom" />
            <col class="col-price" />
            <col class="col-total" />
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>Description</th>
              <th>Specification</th>
              <th class="th-r">Qty</th>
              <th>UOM</th>
              <th class="th-r">Unit Price</th>
              <th class="th-r">Total</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(line, i) in po.lines" :key="line.id" :class="i % 2 === 1 ? 'row-alt' : ''">
              <td class="td-idx">{{ i + 1 }}</td>
              <td class="td-desc-cell">
                <div class="td-desc">{{ line.description }}</div>
              </td>
              <td class="td-spec-cell">
                <span v-if="line.specification" class="td-spec">{{ line.specification }}</span>
                <span v-else class="dim">—</span>
              </td>
              <td class="td-r td-qty">{{ line.quantity }}</td>
              <td class="td-uom">{{ line.uom || '—' }}</td>
              <td class="td-r td-money">{{ formatCurrency(line.unit_price, po.currency) }}</td>
              <td class="td-r td-money td-total">{{ formatCurrency(line.total, po.currency) }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="tfoot-row tfoot-sub">
              <td colspan="6" class="tfoot-label">Subtotal</td>
              <td class="tfoot-value">{{ formatCurrency(po.subtotal, po.currency) }}</td>
            </tr>
            <tr v-if="po.tax_amount" class="tfoot-row tfoot-sub">
              <td colspan="6" class="tfoot-label">Tax / VAT</td>
              <td class="tfoot-value">{{ formatCurrency(po.tax_amount, po.currency) }}</td>
            </tr>
            <tr class="tfoot-row tfoot-grand">
              <td colspan="6" class="tfoot-label tfoot-label-grand">Total</td>
              <td class="tfoot-value tfoot-value-grand">{{ formatCurrency(po.total, po.currency) }}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Notes -->
      <div v-if="po.notes" class="doc-notes-box" style="margin-top:16px">
        <div class="doc-notes-label">Internal Notes</div>
        <div class="doc-notes-text">{{ po.notes }}</div>
      </div>

      <!-- Signature block -->
      <div class="doc-divider doc-divider--dashed"></div>
      <div class="doc-sig-grid">
        <div class="doc-sig-item">
          <div class="doc-sig-line"></div>
          <div class="doc-sig-label">Prepared by</div>
          <div class="doc-sig-name" v-if="creatorName">{{ creatorName }}</div>
          <div class="doc-sig-sub" v-if="po.created_at">{{ formatDate(po.created_at) }}</div>
        </div>
        <div class="doc-sig-item">
          <div class="doc-sig-line"></div>
          <div class="doc-sig-label">Approved by</div>
          <template v-if="approvers.length">
            <div v-for="a in approvers" :key="a.id" class="doc-sig-approver">
              <div class="doc-sig-name">{{ a.name }}</div>
              <div class="doc-sig-sub" v-if="a.at">{{ formatDate(a.at) }}</div>
            </div>
          </template>
          <div v-else class="doc-sig-sub">—</div>
        </div>
      </div>

      <!-- Description / Instructions -->
      <div v-if="po.description" class="doc-notes-box" style="margin-top:24px">
        <div class="doc-notes-label">Instructions</div>
        <div class="doc-notes-text" v-html="po.description"></div>
      </div>

      <!-- Footer -->
      <div class="doc-footer">
        Generated {{ formatDate(new Date().toISOString()) }} · {{ po.po_number }} · {{ business.name }}
      </div>

    </div>

    <div v-else class="print-loading no-print">
      <p>Purchase order not found.</p>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import url from '../../../main';
import { useToast } from 'vue-toastification';

export default {
  name: 'PrintPO',
  setup() { return { toast: useToast() }; },

  data() {
    return {
      po:       null,
      business: {},
      loading:  true,
      hostName: url.hostName,
    };
  },

  computed: {
    creatorName() {
      if (!this.po?.creator) return null;
      const { first_name, last_name } = this.po.creator;
      return [first_name, last_name].filter(Boolean).join(' ') || null;
    },
    approvers() {
      const steps = this.po?.approval_request?.steps ?? [];
      return steps
        .filter(s => s.status === 'approved')
        .map(s => ({
          id:   s.id,
          name: s.acted_by?.full_name
                || [s.acted_by?.first_name, s.acted_by?.last_name].filter(Boolean).join(' ')
                || s.position_name
                || '—',
          at:   s.acted_at,
        }));
    },
  },

  mounted() {
    Promise.all([this.fetchPO(), this.fetchBusiness()])
      .finally(() => { this.loading = false; });
  },

  methods: {
    async fetchPO() {
      try {
        const { data } = await axios.get(`/procurement/purchase-orders/${this.$route.params.id}`);
        this.po = data.data;
      } catch {
        this.toast.error('Failed to load purchase order');
      }
    },

    async fetchBusiness() {
      try {
        const { data } = await axios.get('/admin/business/user/active-business');
        this.business = data.data ?? {};
      } catch { /* non-critical */ }
    },

    printDoc() {
      const style = document.createElement('style');
      style.id = '__po-print-style';
      style.innerHTML = `
        @page { size: A4; margin: 14mm; }
        @media print {
          body > * { visibility: hidden !important; }
          #printMe, #printMe * { visibility: visible !important; }
          #printMe { position: absolute; left: 0; top: 0; width: 100%; background: white; }
        }
      `;
      document.head.appendChild(style);
      window.print();
      setTimeout(() => document.getElementById('__po-print-style')?.remove(), 1200);
    },

    formatStatus(s) {
      return {
        draft: 'Draft', issued: 'Issued',
        partially_received: 'Partially Received', received: 'Received',
        cancelled: 'Cancelled',
      }[s] ?? (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    },

    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },

    formatCurrency(amount, currency) {
      const sym = { NGN: '₦', GBP: '£', USD: '$', EUR: '€' }[currency] ?? (currency ? `${currency} ` : '');
      const n = parseFloat(amount) || 0;
      return sym + n.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
  },
};
</script>

<style scoped>
/* ── Page shell ───────────────────────────────────────────────────────────── */
.print-page-wrap { background: #f0f4f8; min-height: 100vh; padding: 20px; }

.print-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  max-width: 860px; margin: 0 auto 20px; gap: 10px;
}
.toolbar-right { display: flex; gap: 10px; }
.toolbar-btn {
  display: inline-flex; align-items: center; gap: 6px;
  background: #fff; color: #1a2b45; border: 1.5px solid #dde5f0;
  font-size: 13px; font-weight: 600; padding: 9px 18px; border-radius: 8px; cursor: pointer;
}
.toolbar-btn:hover { background: #f4f7fb; }
.toolbar-btn--primary { background: #004AAD; color: #fff; border-color: #004AAD; }
.toolbar-btn--primary:hover { background: #003a8c; }

.print-loading { max-width: 860px; margin: 60px auto; text-align: center; color: #7a90b0; display: flex; flex-direction: column; align-items: center; gap: 14px; }
.spinner { width: 32px; height: 32px; border: 3px solid #e8edf5; border-top-color: #004AAD; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Document ─────────────────────────────────────────────────────────────── */
.document {
  max-width: 860px; margin: 0 auto;
  background: #fff; border-radius: 12px; padding: 48px 56px;
  box-shadow: 0 4px 24px rgba(0,0,0,.08);
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-size: 13px; color: #1a2b45;
}

/* Header */
.doc-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
.doc-brand  { display: flex; align-items: center; gap: 14px; }
.doc-logo   { height: 56px; width: auto; object-fit: contain; }
.doc-logo-placeholder {
  width: 56px; height: 56px; border-radius: 10px; background: #004AAD;
  color: #fff; font-size: 22px; font-weight: 700;
  display: flex; align-items: center; justify-content: center;
}
.doc-company-name { font-size: 17px; font-weight: 700; color: #1a2b45; }
.doc-company-sub  { font-size: 12px; color: #7a90b0; margin-top: 2px; }

.doc-title-block { text-align: right; }
.doc-title {
  font-size: 22px; font-weight: 800; color: #004AAD;
  letter-spacing: .04em; text-transform: uppercase; margin-bottom: 12px;
}
.doc-meta-table { margin-left: auto; border-collapse: collapse; }
.dmt-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em; color: #7a90b0; padding: 3px 10px 3px 0; white-space: nowrap; }
.dmt-value { font-size: 13px; color: #1a2b45; font-weight: 600; padding: 3px 0; }

/* Dividers */
.doc-divider         { border: none; border-top: 2px solid #e4eaf2; margin: 24px 0; }
.doc-divider--dashed { border-top-style: dashed; border-color: #c8d5e8; }

/* Vendor / delivery two-column */
.doc-two-col { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 24px; }
.doc-address-block { display: flex; flex-direction: column; gap: 4px; }
.doc-address-head { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #aab8cc; margin-bottom: 6px; }
.doc-address-name { font-size: 14px; font-weight: 700; color: #1a2b45; }
.doc-address-line { font-size: 12.5px; color: #4b5c73; }
.doc-terms-block  { display: flex; flex-direction: column; }
.doc-terms-row    { display: flex; flex-direction: column; gap: 2px; margin-bottom: 10px; }
.doc-terms-label  { font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #aab8cc; }
.doc-terms-val    { font-size: 13px; font-weight: 600; color: #1a2b45; }

/* Section label */
.doc-section-label { font-size: 14px; font-weight: 700; color: #1a2b45; margin-bottom: 6px; }
.doc-description   { font-size: 13px; color: #4b5c73; margin-bottom: 16px; }

/* Line items table header row */
.table-section-head { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
.table-section-title { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #1a2b45; }
.table-count-badge { background: #eef2f7; color: #64748b; font-size: 11px; font-weight: 600; border-radius: 20px; padding: 2px 9px; }

/* Table */
.doc-table-wrap { border: 1px solid #d5dfe9; border-radius: 10px; overflow: hidden; }
.doc-table { width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 12.5px; }

.col-no    { width: 4%; }
.col-desc  { width: 28%; }
.col-spec  { width: 20%; }
.col-qty   { width: 7%; }
.col-uom   { width: 7%; }
.col-price { width: 16%; }
.col-total { width: 18%; }

.doc-table thead tr { background: #1a2b45; }
.doc-table th {
  padding: 11px 14px; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .07em;
  color: rgba(255,255,255,.7); text-align: left; border: none;
  overflow: hidden; white-space: nowrap;
}
.th-r { text-align: right; }

.doc-table tbody tr           { background: #fff; }
.doc-table tbody tr.row-alt   { background: #f7f9fc; }
.doc-table tbody tr:not(:last-child) td { border-bottom: 1px solid #e8edf3; }
.doc-table td { padding: 11px 14px; vertical-align: middle; }

.td-idx  { color: #b0bdd0; font-size: 11.5px; font-weight: 700; text-align: center; }
.td-desc-cell { vertical-align: top; padding-top: 12px; }
.td-spec-cell { vertical-align: top; padding-top: 12px; }
.td-desc { font-weight: 600; color: #1a2b45; font-size: 13px; word-break: break-word; line-height: 1.4; }
.td-spec { font-size: 11.5px; color: #8fa0b5; line-height: 1.4; word-break: break-word; }
.td-r    { text-align: right; }
.td-qty  { font-size: 13px; font-weight: 600; color: #1a2b45; font-variant-numeric: tabular-nums; }
.td-uom  { font-size: 11.5px; color: #8fa0b5; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.td-money { font-size: 12.5px; color: #1a2b45; font-variant-numeric: tabular-nums; white-space: nowrap; }
.td-total { font-weight: 700; font-size: 13px; color: #004AAD; }

/* Totals footer */
.tfoot-row  td { border-top: 1px solid #d5dfe9; border-bottom: none; }
.tfoot-sub  { background: #f8fafc; }
.tfoot-grand { background: #f0f5ff; }
.tfoot-label {
  padding: 10px 14px; font-size: 10px; font-weight: 700;
  text-transform: uppercase; letter-spacing: .07em;
  color: #64748b; text-align: right;
}
.tfoot-label-grand { color: #1a2b45; font-size: 11px; }
.tfoot-value { padding: 10px 14px; font-size: 14px; font-weight: 700; color: #1a2b45; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.tfoot-value-grand { font-size: 16px; font-weight: 800; color: #004AAD; }

/* Notes */
.doc-notes-box { background: #f8fafc; border: 1px solid #e4eaf2; border-radius: 8px; padding: 14px 16px; }
.doc-notes-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #7a90b0; margin-bottom: 6px; }
.doc-notes-text  { font-size: 13px; color: #1a2b45; }

/* Signature block */
.doc-sig-grid     { display: grid; grid-template-columns: repeat(2, 1fr); gap: 48px; margin-top: 8px; }
.doc-sig-line     { border-bottom: 1.5px solid #1a2b45; margin-bottom: 8px; height: 40px; }
.doc-sig-label    { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #1a2b45; }
.doc-sig-name     { font-size: 13px; font-weight: 600; color: #1a2b45; margin-top: 4px; }
.doc-sig-sub      { font-size: 11px; color: #aab8cc; margin-top: 2px; }
.doc-sig-approver { margin-top: 6px; }

/* Status pills */
.status-pill { display: inline-block; font-size: 11px; font-weight: 600; border-radius: 20px; padding: 2px 10px; }
.status-pill--draft              { background: #f1f5ff; color: #004AAD; }
.status-pill--issued             { background: #fef9c3; color: #854d0e; }
.status-pill--partially_received { background: #fef3c7; color: #92400e; }
.status-pill--received           { background: #dcfce7; color: #15803d; }
.status-pill--cancelled          { background: #fee2e2; color: #dc2626; }

.mono    { font-family: monospace; }
.mono-sm { font-family: monospace; font-size: 12px; }
.dim     { color: #aab8cc; }
.me-1    { margin-right: 4px; }

/* Footer */
.doc-footer { margin-top: 32px; text-align: center; font-size: 10.5px; color: #aab8cc; }

/* ── Print overrides ─────────────────────────────────────────────────────── */
@media print {
  .no-print        { display: none !important; }
  .print-page-wrap { background: white; padding: 0; }
  .document        { box-shadow: none; border-radius: 0; padding: 0; max-width: 100%; }
}

@media (max-width: 700px) {
  .document        { padding: 24px 20px; }
  .doc-header      { flex-direction: column; }
  .doc-title-block { text-align: left; }
  .doc-meta-table  { margin-left: 0; }
  .doc-two-col     { grid-template-columns: 1fr; }
  .doc-sig-grid    { grid-template-columns: 1fr; gap: 24px; }
  /* hide Specification (col 3) and UOM (col 5) on small screens */
  .doc-table th:nth-child(3), .doc-table td:nth-child(3),
  .doc-table th:nth-child(5), .doc-table td:nth-child(5) { display: none; }
  .col-spec, .col-uom { display: none; }
  .col-desc  { width: 45%; }
  .col-qty   { width: 10%; }
  .col-price { width: 20%; }
  .col-total { width: 21%; }
}
</style>
